import React, { SetStateAction, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AuthClient,
  LoginUserDTO,
  AuthDTO,
  TokenExchangeDTO,
  ApiResponse_string,
} from '../../services/apiClient';
import AuthLayout from '../../components/layout/AuthLayout';
import FormField from '../../components/common/FormField';
import Button from '../../components/common/Button';
import { API_BASE_URL } from '../../config/config';
import axios from 'axios';
import AppleIcon from '../../components/icons/AppleIcon';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { GOOGLE_CLIENT_ID } from '../../config/config';
import GoogleLoginButton from '../../components/auth/GoogleLoginButton';

const Login: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [twoFactorCode, setTwoFactorCode] = useState<string>('');
  const [showTwoFactorInput, setShowTwoFactorInput] = useState<boolean>(false);
  const [tempAccessToken, setTempAccessToken] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();

  const apiClient = new AuthClient(
    API_BASE_URL,
    axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      transformResponse: []
    })
  );

  const from =
    (location.state as { from?: Location })?.from?.pathname || '/admin/dashboard';

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      navigate(from);
    }
  }, [from, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const loginData: LoginUserDTO = { email, password };

    try {
      setLoading(true);
      const response = await apiClient.generateToken(loginData);
      setLoading(false);

      handleSignInResponse(response, 'Invalid credentials. Please try again.');
      
    } catch (error: any) {
      setLoading(false);
      setErrorMessage(error.message);
    }
  };

  const handleTwoFactorVerification = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (!tempAccessToken) {
        setErrorMessage('Session expired. Please log in again.');
        return;
      }

      const response = await apiClient.generateTokenTwoFactor({
        code: twoFactorCode,
        tempToken: tempAccessToken,
        email: email
      });

      if (response && response.success && response.data) {
        const responseData = response.data as AuthDTO;

        // Save the final tokens
        localStorage.setItem('token', responseData.accessToken ?? '');
        localStorage.setItem('refresh_token', responseData.refreshToken ?? '');
        setTempAccessToken(null);
        navigate(from);
      } else {
        setErrorMessage('Invalid two-factor authentication code. Please try again.');
      }
    } catch (error) {
      setErrorMessage('Failed to verify two-factor authentication. Please try again.');
    }
  };

  const handleGoogleSignIn = async (token: TokenExchangeDTO) => {
    try {
      setLoading(true);
      const response = await apiClient.googleTokenExchange(token);
      setLoading(false);

      handleSignInResponse(response, 'Unable to sign in with Google.');

    } catch (error: any) {
      setLoading(false);
      setErrorMessage(error.message);
    }
  };

  const handleSignInResponse = (response: ApiResponse_string, errorMessage: SetStateAction<string|null>) => {
    if (response && response.success && response.data) {
      setErrorMessage('');

      const responseData = response.data as AuthDTO;

      if (responseData.twoFactorEnabled) {
        // 2FA is enabled, show the 2FA input field
        setTempAccessToken(responseData.accessToken ?? '');
        setShowTwoFactorInput(true);
      } else {
        // 2FA is not enabled, proceed with login
        localStorage.setItem('token', responseData.accessToken ?? '');
        localStorage.setItem(
          'refresh_token',
          responseData.refreshToken ?? '',
        );
        navigate(from);
      }
    } else {
      setErrorMessage(errorMessage);
    }
  }

  return (
    <AuthLayout 
        title="Login" 
        description="Sign in to access your account."
        >
      <div className="mt-8">
        {errorMessage && (
          <div className="mb-4 text-sm text-red-600">{errorMessage}</div>
        )}

        {!showTwoFactorInput ? (
          <form onSubmit={handleSubmit}>
            <FormField
              id="email"
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="example@example.com"
            />
            <div className="mt-6">
              <FormField
                id="password"
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your Password"
              />
            </div>
            <div className="mt-6">
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Loading...' : 'Sign In'}
              </Button>
            </div>
            <div className="mt-6">
              <span>or</span>
            </div>
            <div className="mt-6">
              {/*Google*/}
              <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
                <GoogleLoginButton
                  handleGoogleSignIn={handleGoogleSignIn}
                  setErrorMessage={setErrorMessage}
                />
              </GoogleOAuthProvider>
            </div>
          </form>
        ) : (
          <form onSubmit={handleTwoFactorVerification}>
            <div className='text-left'>
              <p className="text-left text-gray-700 dark:text-gray-300 mb-4">
                Enter the 2FA code from your authentication app.
              </p>
              <FormField
                id="twoFactorCode"
                label=""
                type="text"
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value)}
                maxLength={6}
                placeholder="6-digit code"
              />
            </div>
            <div className="mt-6">
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Verifying...' : 'Verify'}
              </Button>
            </div>
          </form>
        )}

        <p className="mt-6 text-sm text-center text-gray-400">
          Forgot your password?{' '}
          <Link
            to={'/forgot-password'}
            className="text-black focus:outline-none focus:underline hover:underline"
          >
            Reset it now
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
};

export default Login;
